import React from "react";
import { Menu, MenuItem } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarIcon from "@mui/icons-material/Star";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { LegacyTokens } from "../../../../theme/b13Tokens";

// Batch 6B — kebab for a single template row.
//
// Five items per BATCH_6B follow-up (user Option 2 on §10):
//   Duplicate
//   Set as default   (hidden when isDefault)
//   Archive / Restore (label branches on row.status === 'archived')
//   Delete           (confirms; blocked when in use)
//
// Lock/Unlock is NOT here. It was in an earlier draft as a real-but-
// pending stub with a toast; the toast read as dev copy on a client
// screen (same rule that killed the "Rating Scale UI pending client
// reference" placeholder). It comes back in the isLocked batch, when
// the schema column and endpoint ship and the item is a real action.
//
// The dormant padlock indicator on the row itself stays — that is a
// display-only element and needs no change when the column ships.

export type TemplateRowKebabProps = {
  anchor: HTMLElement | null;
  row: any | null;
  onClose: () => void;
  onDuplicate: () => void;
  onSetDefault: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
};

export default function TemplateRowKebab({
  anchor, row, onClose,
  onDuplicate, onSetDefault, onArchive, onRestore, onDelete,
}: TemplateRowKebabProps) {
  if (!row) return null;

  const isDefault  = !!row.isDefault;
  const isArchived = row.status === "archived";

  return (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: `${LegacyTokens.radius.control}px`,
          minWidth: 220,
          boxShadow: "0 4px 12px rgba(15, 61, 46, 0.10)",
        },
      }}
    >
      <MenuItem onClick={() => { onClose(); onDuplicate(); }}>
        <ContentCopyIcon fontSize="small" style={{ marginRight: 8, color: LegacyTokens.ink3 }} />
        Duplicate
      </MenuItem>

      {/* Set as default — hidden when this row IS the default. */}
      {!isDefault && (
        <MenuItem onClick={() => { onClose(); onSetDefault(); }}>
          <StarIcon fontSize="small" style={{ marginRight: 8, color: LegacyTokens.emerald }} />
          Set as default
        </MenuItem>
      )}

      {/* Archive ⇄ Restore — label branches on status. */}
      {isArchived ? (
        <MenuItem onClick={() => { onClose(); onRestore(); }}>
          <UnarchiveIcon fontSize="small" style={{ marginRight: 8, color: LegacyTokens.ink3 }} />
          Restore
        </MenuItem>
      ) : (
        <MenuItem onClick={() => { onClose(); onArchive(); }}>
          <ArchiveIcon fontSize="small" style={{ marginRight: 8, color: LegacyTokens.ink3 }} />
          Archive
        </MenuItem>
      )}

      <MenuItem onClick={() => { onClose(); onDelete(); }} sx={{ color: "error.main" }}>
        <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
        Delete
      </MenuItem>
    </Menu>
  );
}
